import React, { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { constants, imagePaths, permissionType } from 'src/lib/utils/constants'
import { useSegmentTemplates } from 'src/lib/hooks/useSegmentTemplates'
import { Input, IconButton, Tabs, TabsList, TabsTrigger } from '@shopflo/ui'
import { PingPong, X } from '@phosphor-icons/react'
import useGTM from 'src/lib/hooks/useGTM'
import { SegmentTemplateType } from 'src/lib/types/segment'
import { useUser } from 'src/lib/contexts/UserContext'
import { Tooltip } from '@mui/material'
import { useLocale } from 'src/lib/hooks/useLocal'
import ImageContainer from '../common/ImageContainer'
import { DialogSegmentTypeCards, PageSegmentTypeCards } from './SegmentTypeCards'

export interface SegmentTemplateVariable {
  key: string
  type: 'NUMBER' | 'STRING' | 'DATE'
  label: string
  required: boolean
  default_value?: number | string
}

interface TemplatesViewProps {
  openType: 'page' | 'dialog' | 'creation-flow'
  onCreateStaticSegment: () => void
  onCreateDynamicSegment: () => void
  onCreateCustomerTagSegment?: () => void
  onTemplateSelect: (template: SegmentTemplateType) => void
  onClose?: () => void
}

const TemplatesView: React.FC<TemplatesViewProps> = ({
  openType,
  onCreateStaticSegment,
  onCreateDynamicSegment,
  onCreateCustomerTagSegment,
  onTemplateSelect,
  onClose
}) => {
  const { t } = useLocale()
  const { sendGTMTrigger } = useGTM()
  const [templateSearchText, setTemplateSearchText] = useState<string>('')
  const [activeFilter, setActiveFilter] = useState<string>('all')
  const { canAccess } = useUser()
  const hasDynamicSegmentsPermission = canAccess(permissionType.DYNAMIC_SEGMENTS, 0)
  const { templates, categories, isLoading, error } = useSegmentTemplates()
  const isPage = openType === 'page'
  const [isHighlightVisible, setIsHighlightVisible] = useState(false)

  // Sync dismissed flag from localStorage after mount — unreadable during prerender, so seeding
  // useState from it would mismatch SSR HTML.
  useEffect(() => {
    let cancelled = false
    const dismissed =
      globalThis?.window?.localStorage.getItem(constants.SHOPIFY_TAG_CARD_HIGHLIGHT_DISMISSED) === 'true'
    if (!cancelled) setIsHighlightVisible(!dismissed)
    return () => {
      cancelled = true
    }
  }, [])

  const searchQuery = templateSearchText.toLowerCase()
  const filteredTemplates = templates.filter(template => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchQuery) || template.description.toLowerCase().includes(searchQuery)
    const matchesFilter = activeFilter === 'all' || template.category === activeFilter
    return matchesSearch && matchesFilter
  })

  const handleTemplateClick = (template: SegmentTemplateType) => {
    sendGTMTrigger('segment_template_selected', 'click', { templateId: template.id })
    onTemplateSelect(template)
  }

  const typePickerProps = {
    hasDynamicSegmentsPermission,
    onCreateStaticSegment,
    onCreateDynamicSegment,
    onCreateCustomerTagSegment: () => {
      setIsHighlightVisible(false)
      globalThis?.window?.localStorage.setItem(constants.SHOPIFY_TAG_CARD_HIGHLIGHT_DISMISSED, 'true')
      onCreateCustomerTagSegment?.()
    },
    showShopifyHighlight: isHighlightVisible
  }

  const templateSearchControls = (
    <>
      <div className='w-full'>
        <Input
          variant='filled'
          dimension='md'
          placeholder={t('segment_creation.or_search_template')}
          value={templateSearchText}
          onChange={e => setTemplateSearchText(e.target.value)}
          className='w-full'
        />
      </div>
      <Tabs value={activeFilter} onValueChange={setActiveFilter}>
        <TabsList variant='neutral' size='sm' gap='0.5'>
          <TabsTrigger value='all'>{t('segment_creation.all_filter')}</TabsTrigger>
          {categories.map(category => (
            <TabsTrigger key={category} value={category}>
              {category.charAt(0).toUpperCase() + category.slice(1).replace(/_/g, ' ')}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </>
  )

  const renderTemplateList = () => {
    if (isLoading) {
      return (
        <motion.div
          key='templates-skeleton'
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.2, ease: 'easeOut' } }}
          transition={{ duration: 0 }}
          className='flex min-w-0 flex-col gap-0 overflow-x-hidden'
          aria-busy='true'
        >
          {(
            [
              ['w-[88%]', 'opacity-100'],
              ['w-[58%]', 'opacity-70'],
              ['w-[96%]', 'opacity-40'],
              ['w-[70%]', 'opacity-10']
            ] as const
          ).map(([widthClass, rowOpacityClass], i) => (
            <div
              key={i}
              className={`flex min-w-0 max-w-full w-full flex-row items-center gap-2 px-2 py-1.5 ${rowOpacityClass}`}
            >
              <div className='h-5 w-5 shrink-0 animate-pulse rounded-sm bg-zinc-200/90' />
              <div className='min-w-0 flex-1'>
                <div className={`h-4 max-w-full animate-pulse rounded-md bg-zinc-200/90 ${widthClass}`} />
              </div>
            </div>
          ))}
        </motion.div>
      )
    }

    if (error) {
      return (
        <motion.div
          key='templates-content'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.28, ease: 'easeOut' } }}
          exit={{ opacity: 1, transition: { duration: 0 } }}
          className='flex min-w-0 flex-col space-y-0 overflow-x-hidden'
        >
          <div className='flex items-center justify-center p-8 text-zinc-500'>
            <span className='text-text-sm-regular text-zinc-500'>
              {t('segment_creation.error_loading_templates')}
            </span>
          </div>
        </motion.div>
      )
    }

    if (filteredTemplates.length === 0) {
      return (
        <motion.div
          key='templates-content'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.28, ease: 'easeOut' } }}
          exit={{ opacity: 1, transition: { duration: 0 } }}
          className='flex min-w-0 flex-col space-y-0 overflow-x-hidden'
        >
          <div className='flex items-center justify-center p-8 text-zinc-500'>
            <span className='text-text-sm-regular text-zinc-500'>{t('segment_creation.no_templates_found')}</span>
          </div>
        </motion.div>
      )
    }

    return (
      <motion.div
        key='templates-content'
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.28, ease: 'easeOut' } }}
        exit={{ opacity: 1, transition: { duration: 0 } }}
        className='flex min-w-0 flex-col space-y-0 overflow-x-hidden'
      >
        {filteredTemplates.map(template => (
          <Tooltip
            key={template.id}
            title={!hasDynamicSegmentsPermission ? t('segment_creation.upgrade_plan_message') : ''}
          >
            <button
              onClick={() => handleTemplateClick(template)}
              className='flex min-w-0 max-w-full w-full flex-row items-center gap-2 px-2 py-1.5 text-left hover:bg-zinc-500/5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed'
              disabled={!hasDynamicSegmentsPermission}
            >
              <PingPong size={20} className='shrink-0 text-zinc-400' weight='regular' />
              <span className='min-w-0 flex-1 truncate text-text-sm-regular text-zinc-600'>{template.name}</span>
            </button>
          </Tooltip>
        ))}
      </motion.div>
    )
  }

  return (
    <div
      className={`flex flex-col w-full min-w-0 overflow-visible ${
        openType === 'dialog' ? 'max-w-full items-stretch' : 'max-w-[840px] mx-auto mt-12 items-center justify-center'
      }`}
    >
      {onClose && (
        <div className='flex justify-end px-5 pt-4'>
          <IconButton
            IconComponent={X}
            variant='outline'
            size='sm'
            onClick={onClose}
            tooltip={{ text: t('segment_creation.close'), side: 'left' }}
          />
        </div>
      )}

      {openType !== 'creation-flow' && (
        <>
          <div className='flex w-full min-w-0 flex-col items-center gap-2 px-5 pt-4 pb-6'>
            {isPage ? (
              <div className='flex items-center justify-center h-[100px]'>
                <ImageContainer
                  height={100}
                  imageSource={'/images/chocolate-bar.png'}
                  defaultImage={'/images/image-placeholder.svg'}
                  altText={'Shopify Bundles'}
                />
              </div>
            ) : (
              <div className='flex items-center justify-center w-[76px] h-[76px]'>
                <ImageContainer
                  height={76}
                  width={76}
                  imageSource={'/images/chocolate-bar.png'}
                  defaultImage={'/images/image-placeholder.svg'}
                  altText={'Shopify Bundles'}
                />
              </div>
            )}
            <h2 className='text-text-lg-semibold text-zinc-800 text-center'>
              {t('segment_creation.group_customers_header')}
            </h2>
          </div>

          <div
            className={
              isPage
                ? 'sticky top-0 z-10 flex w-full min-w-0 flex-col bg-zinc-50 px-5 pt-4 pb-3'
                : 'flex w-full min-w-0 flex-col gap-6 px-5 py-4'
            }
          >
            {isPage ? <PageSegmentTypeCards {...typePickerProps} /> : <DialogSegmentTypeCards {...typePickerProps} />}
            {isPage && (
              <>
                <div className='h-px my-4 w-full bg-zinc-950/5' />
                <div className='mx-auto flex w-[640px] max-w-full min-w-0 flex-col space-y-3 px-4'>
                  {templateSearchControls}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {!isPage && openType !== 'creation-flow' && <div className='h-px mb-4 w-full bg-zinc-950/5' />}

      <div className='flex w-[640px] max-w-full min-w-0 flex-col space-y-3 px-4'>
        {!isPage && templateSearchControls}
        <div className='relative min-h-[7.5rem] min-w-0'>
          <AnimatePresence mode='popLayout'>{renderTemplateList()}</AnimatePresence>
        </div>
      </div>
    </div>
  )
}

export default TemplatesView
